import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WizardFormData } from "../../types/wizard.types";
import { GymMember } from "../../types/wizard-demo.types";
import { gymMembers } from "../../utils/mockMembers";
import { AppContext } from "../../../../context/app";
import { Button } from "@pushpress/pushpress-ui";
import { Download, Copy } from "lucide-react";

interface CommittedClubWebDemoProps {
  formData: WizardFormData;
}

interface MemberProgress {
  member: GymMember;
  checkIns: number;
  isInCommittedClub: boolean;
  hasJustJoined: boolean;
}

// Animation timing configuration - adjust these to change speed
const ANIMATION_CONFIG = {
  INITIAL_DELAY: 1000, // Wait before starting
  ROUND_DELAY: 1500, // Delay between rounds
  ROUNDS_COUNT: 8, // Number of animation rounds
  TOAST_DELAY: 400, // Delay before showing toast
  TOAST_DURATION: 2500, // How long toast shows
  CLEANUP_DELAY: 300, // Delay before removing highlight
};

const CommittedClubWebDemo: React.FC<CommittedClubWebDemoProps> = ({
  formData,
}) => {
  const app = useContext(AppContext);
  const { client } = app;
  const { company } = client;
  const [memberProgress, setMemberProgress] = useState<MemberProgress[]>([]);
  const [committedClubCount, setCommittedClubCount] = useState(2);
  const [showToast, setShowToast] = useState(false);
  const [toastMember, setToastMember] = useState<GymMember | null>(null);

  const currentDate = new Date();
  const monthYear = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  useEffect(() => {
    const selectedMembers = [...gymMembers.slice(0, 5)];

    const initialProgress: MemberProgress[] = [
      {
        member: selectedMembers[0], // Sarah Chen - already in committed club
        checkIns: 12,
        isInCommittedClub: true,
        hasJustJoined: false,
      },
      {
        member: selectedMembers[1], // Tim Williams - already in committed club
        checkIns: 8,
        isInCommittedClub: true,
        hasJustJoined: false,
      },
      {
        member: selectedMembers[2], // Alex Rodriguez - will be animated (5→8)
        checkIns: 5,
        isInCommittedClub: false,
        hasJustJoined: false,
      },
      {
        member: selectedMembers[3], // Emma Williams - won't be animated
        checkIns: 4,
        isInCommittedClub: false,
        hasJustJoined: false,
      },
      {
        member: selectedMembers[4], // Emiton Alverson - will be animated (3→8)
        checkIns: 3,
        isInCommittedClub: false,
        hasJustJoined: false,
      },
    ];

    setMemberProgress(initialProgress);
  }, []);

  useEffect(() => {
    const animationLoop = async () => {
      await new Promise((resolve) =>
        setTimeout(resolve, ANIMATION_CONFIG.INITIAL_DELAY),
      );

      for (let round = 0; round < ANIMATION_CONFIG.ROUNDS_COUNT; round++) {
        // Check if both target members have reached committed club
        const currentState = memberProgress;
        const emitonState = currentState[4]; // Emiton Alverson
        const alexState = currentState[2]; // Alex Rodriguez

        if (emitonState?.isInCommittedClub && alexState?.isInCommittedClub) {
          break;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, ANIMATION_CONFIG.ROUND_DELAY),
        );

        setMemberProgress((prev) => {
          const updated = [...prev];

          // Predictable sequence: Emiton Alverson (5th) first, then Alex Rodriguez (3rd)
          let memberToUpdate = null;
          let memberIndex = -1;

          const emitonIndex = 4; // Emiton Alverson
          const emitonMember = updated[emitonIndex];
          const alexIndex = 2; // Alex Rodriguez
          const alexMember = updated[alexIndex];

          if (!emitonMember.isInCommittedClub && emitonMember.checkIns < 8) {
            // Keep incrementing Emiton until he reaches 8 (3→4→5→6→7→8)
            memberToUpdate = emitonMember;
            memberIndex = emitonIndex;
          } else if (!alexMember.isInCommittedClub && alexMember.checkIns < 8) {
            // Once Emiton is done, increment Alex until he reaches 8 (5→6→7→8)
            memberToUpdate = alexMember;
            memberIndex = alexIndex;
          }

          if (memberToUpdate && memberIndex !== -1) {
            updated[memberIndex] = {
              ...updated[memberIndex],
              checkIns: updated[memberIndex].checkIns + 1,
            };

            if (updated[memberIndex].checkIns >= 8) {
              updated[memberIndex].isInCommittedClub = true;
              updated[memberIndex].hasJustJoined = true;

              // Timeout sequence for toast notification
              setTimeout(() => {
                setToastMember(updated[memberIndex].member);
                setShowToast(true);
                setCommittedClubCount((prev) => prev + 1);

                setTimeout(() => {
                  setShowToast(false);
                  setToastMember(null);

                  setTimeout(() => {
                    setMemberProgress((current) =>
                      current.map((p) =>
                        p.member.fullName ===
                        updated[memberIndex].member.fullName
                          ? { ...p, hasJustJoined: false }
                          : p,
                      ),
                    );
                  }, ANIMATION_CONFIG.CLEANUP_DELAY);
                }, ANIMATION_CONFIG.TOAST_DURATION);
              }, ANIMATION_CONFIG.TOAST_DELAY);
            }
          }

          return updated;
        });
      }
    };

    if (memberProgress.length > 0) {
      animationLoop();
    }
    // Intentionally only depend on length, not content - animation should only restart when members are added/removed, not when check-ins change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberProgress.length]);

  return (
    <div className="tw-min-h-full tw-bg-gray-50 tw-flex tw-flex-col tw-relative">
      {/* Main Content */}
      <div className="tw-flex-1 tw-p-8 tw-bg-gray-50">
        <div className="tw-max-w-7xl tw-mx-auto tw-space-y-6">
          {/* Content Container */}
          <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
            {/* Results Summary */}
            <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-justify-between tw-mb-6">
              <div>
                <h2 className="tw-text-xl tw-font-semibold tw-text-gray-700 tw-mb-3">
                  {monthYear} Committed Club Results
                </h2>
                <motion.p
                  key={committedClubCount}
                  initial={{ scale: 1.1, opacity: 0.7 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="tw-text-lg tw-text-gray-600"
                >
                  {committedClubCount} people made it into the {company}{" "}
                  Committed Club!
                </motion.p>
              </div>
              <div className="tw-flex tw-space-x-4">
                <Button variant="secondary">
                  <Copy className="tw-w-4 tw-h-4 tw-mr-2" />
                  Copy names
                </Button>
                <Button variant="secondary">
                  <Download className="tw-w-4 tw-h-4 tw-mr-2" />
                  Export
                </Button>
              </div>
            </div>

            {/* All Results Section */}
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                <h3 className="tw-text-lg tw-font-medium tw-text-gray-700">
                  All Results
                </h3>
                <button
                  className="tw-text-sm tw-font-medium tw-hover:tw-underline tw-flex tw-items-center"
                  style={{ color: formData?.color || "#3B82F6" }}
                >
                  <span className="tw-mr-1">↓</span> Sorted by check-ins
                </button>
              </div>

              {/* Table */}
              <div className="tw-bg-white tw-rounded-lg tw-shadow tw-overflow-hidden tw-border tw-border-gray-200">
                {/* Table Header */}
                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-px-6 tw-py-4 tw-bg-gray-50 tw-border-b">
                  <div className="tw-text-sm tw-font-semibold tw-text-gray-700">
                    Name
                  </div>
                  <div className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-text-right">
                    Check-ins
                  </div>
                </div>

                {/* Table Body */}
                <div className="tw-divide-y tw-divide-gray-100">
                  {memberProgress
                    .slice()
                    .sort((a, b) => {
                      // Sort by check-ins descending (highest first)
                      if (b.checkIns !== a.checkIns) {
                        return b.checkIns - a.checkIns;
                      }
                      // If check-ins are equal, sort alphabetically by name
                      return a.member.fullName.localeCompare(b.member.fullName);
                    })
                    .map((progress) => (
                      <motion.div
                        key={progress.member.fullName}
                        layout
                        initial={{ backgroundColor: "transparent" }}
                        animate={{
                          backgroundColor: "transparent",
                          borderColor: "transparent",
                        }}
                        transition={{
                          duration: 0.5,
                          layout: { duration: 0.4, ease: "easeInOut" },
                        }}
                        className="tw-grid tw-grid-cols-2 tw-gap-4 tw-px-6 tw-py-4 tw-border-l-4 tw-text-gray-900"
                      >
                        <div className="tw-flex tw-items-center">
                          <div className="tw-w-10 tw-h-10 tw-rounded-full tw-overflow-hidden tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-mr-4">
                            <img
                              src={progress.member.avatar}
                              alt={progress.member.fullName}
                              className="tw-w-full tw-h-full tw-object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `<span class="tw-text-sm tw-font-medium tw-text-gray-600">${progress.member.initials}</span>`;
                                }
                              }}
                            />
                          </div>
                          <div>
                            <p className="tw-font-medium tw-text-base tw-text-blue-600">
                              {progress.member.fullName}
                            </p>
                            <p className="tw-text-sm tw-text-gray-500">
                              {progress.member.fullName
                                .toLowerCase()
                                .replace(" ", ".") + "@mail.com"}
                            </p>
                          </div>
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end">
                          <div className="tw-flex tw-items-center">
                            <motion.span
                              key={`${progress.member.fullName}-${progress.checkIns}`}
                              initial={{ scale: 1.2, opacity: 0.7 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ duration: 0.3 }}
                              className="tw-text-2xl tw-font-bold tw-mr-3 tw-text-gray-900"
                            >
                              {progress.checkIns}
                            </motion.span>
                            {progress.isInCommittedClub && (
                              <div className="tw-w-8 tw-h-8 tw-bg-green-500 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                                <span className="tw-text-white tw-text-sm">
                                  ✓
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && toastMember && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{
              type: "spring",
              duration: 0.6,
              bounce: 0.3,
            }}
            className="tw-absolute tw-bottom-6 tw-right-6 tw-bg-white tw-rounded-lg tw-shadow-xl tw-border tw-border-gray-200 tw-p-4 tw-max-w-sm tw-z-50"
          >
            <div className="tw-flex tw-items-center tw-space-x-3">
              <div className="tw-flex-shrink-0">
                <div className="tw-w-10 tw-h-10 tw-rounded-full tw-overflow-hidden tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
                  <img
                    src={toastMember.avatar}
                    alt={toastMember.fullName}
                    className="tw-w-full tw-h-full tw-object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<span class="tw-text-sm tw-font-medium tw-text-green-600">${toastMember.initials}</span>`;
                      }
                    }}
                  />
                </div>
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <p className="tw-text-sm tw-text-gray-800">
                  🎉 <strong>{toastMember.firstName}</strong> made it into the{" "}
                  <span className="tw-font-semibold tw-text-green-600">
                    Committed Club
                  </span>{" "}
                  with 8 check-ins!
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommittedClubWebDemo;
